import React, { useMemo, useState } from 'react';
import AvFilters from '@components/AvFilters';
import { AvDateRangePicker, DatePickerStartAdornment, DateValue, ExportButton, Filter } from '@fabric-ui/components';
import { FileFormat } from '@fabric-ui/components/Table';
import { History as HistoryData } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Typography, useTheme } from '@mui/material';
import { useLocation } from '@utils/AvRouter';
import { useMeasurementsList } from '@views/Reports/hooks';
import { useAvContext } from '../../../context/AvContextProvider';
import { ScreenType } from '../../../types/savedViews.types';
import { useExportDashboard } from '../../CustomDashboards/hooks';
import { DashboardType } from '../../CustomDashboards/types';
import { useSavedViews } from '../../Entities/hooks';
import { getLast3MonthsDate, getTableTypeTitle } from '../utils';
import InteractiveView from './interactiveView';
import { getCommonAlerts, getNovelAlerts, getTotalAlerts, getTotalIncident } from './queries';
import { InteractiveWidgetType, SelectedWidgetData } from './types';
import AlertsMappedToTactic from './widgets/alertsByTactic';
import AlertToIncidents from './widgets/alertToIncidents';
import IncidentStatus from './widgets/incidentStatus';
import MeanTimeToRespond from './widgets/meanTimeToRespond';
import ThreatStatus from './widgets/threatStatus';
import Top5AttackedEntities from './widgets/top5AttackedEntities';
import Top5VendorsByAlertsVsIncidents from './widgets/top5VendorsByAlertsVsIncidents';
import TopHighRiskIncidents from './widgets/topHighRiskIncidents';

const TITLE = 'Dashboard';
const projName = 'incidents';
const DEFAULT_FILTER = { 'alert.title': [], 'alert.source_names': [], 'incident.type': [] } as Filter;

const getWidgetTitle = (type: InteractiveWidgetType, selected: SelectedWidgetData): string => {
  switch (type) {
    case InteractiveWidgetType.MeanTimeToRespond:
      return 'Details';
    case InteractiveWidgetType.Top5Vendors:
      return getTableTypeTitle(selected.tableType);
    default:
      return selected.name;
  }
};

const ThreatDashboard = () => {
  const [filters, setFilters] = useState<Filter>(DEFAULT_FILTER);
  const [activeWidget, setActiveWidget] = useState<{
    type: InteractiveWidgetType;
    selected: SelectedWidgetData;
    title: string;
  } | null>(null);
  const [dateRange, setDateRange] = useState<Date[]>([getLast3MonthsDate(), new Date()]);

  const handleWidgetSelect = (type: InteractiveWidgetType, selected: SelectedWidgetData) => {
    if (!selected) {
      setActiveWidget(null);
      return;
    }

    const title = getWidgetTitle(type, selected);
    setActiveWidget({ type, selected, title });
  };

  const location = useLocation();

  const { SelectedView, SaveViewButtons, setSelectedViewChanged } = useSavedViews({
    viewScreenType: ScreenType.ThreatDashboard,
    filters,
    setFilters,
  });

  const onFilterChange = newFilters => {
    setFilters({ ...filters, ...newFilters });
    setSelectedViewChanged(true);
  };
  const {
    accountEntities: { aggProjs },
  } = useAvContext();
  const { data: measurements = [] } = useMeasurementsList({ entityName: aggProjs[projName]?.entityTypeId?.name });

  const typeMap = useMemo(
    () => measurements.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), aggProjs[projName as string]?.typeMap),
    []
  );

  const filterContext = { filters, dateRange, typeMap };
  const { exportDashboard, isLoading: isExportingDashboard } = useExportDashboard({
    dashboardExportDto: {
      dashboardType: DashboardType.THREAT_DASHBOARD,
      fileFormat: FileFormat.PDF_FORMAT,
      searchParams: location.searchStr,
    },
  });

  const theme = useTheme();

  const widgetBoxStyle = {
    flex: 1,
    borderRadius: '2px',
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.colors.neutrals[100],
    overflow: 'auto',
    flexShrink: 0,
  };

  const statuses = {
    Total_Alerts: <ThreatStatus filterContext={filterContext} totalFunc={getTotalAlerts} title="Total Alerts" />,
    Total_Incidents: <ThreatStatus filterContext={filterContext} totalFunc={getTotalIncident} title="Total Incidents" />,
    Common_Alerts: (
      <ThreatStatus
        filterContext={filterContext}
        totalFunc={getCommonAlerts}
        title="Common Alerts"
        dataConverter={data => data.reduce((res, d) => res + d.value, 0)}
      />
    ),
    Novel_Alerts: <ThreatStatus filterContext={filterContext} totalFunc={getNovelAlerts} title="Novel Alerts" />,
  };

  return (
    <Box sx={{ ...flex.col, width: '100%', gap: 2 }}>
      <Box sx={{ ...flex.justifyBetween }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {TITLE}
        </Typography>

        <AvDateRangePicker
          showDropdownIcon
          inputSx={{ width: '500px' }}
          value={{ from: dateRange[0] }}
          icon={<DatePickerStartAdornment tooltipText="Historical Data" icon={<HistoryData />} />}
          onChange={timeRange => {
            if (timeRange && timeRange.value) {
              setDateRange([(timeRange.value as DateValue).from, (timeRange.value as DateValue).to ?? new Date()]);
            } else {
              setDateRange([]);
            }
          }}
          showRelativeOptions
          isRange
          allowFutureDates={false}
          showOptions={false}
        />
      </Box>
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        {SelectedView}
        <Box sx={{ flexGrow: 1 }} />
        {SaveViewButtons}
      </Box>
      <Box sx={{ ...flex.justifyBetween }}>
        <AvFilters
          setFilters={setFilters}
          disabled={activeWidget !== null}
          updateFilters={(key, value) => onFilterChange({ [key]: value })}
          filters={filters}
          onClearFilters={() => {
            setFilters(DEFAULT_FILTER);
          }}
          dims={aggProjs.uber_alerts.fieldList.INTERACTIVE}
          isFilterExpression
          isGlobalFilter
          isDraggable
        />
        <ExportButton
          customTooltipText={isExportingDashboard ? 'Exporting dashboard...' : 'Export dashboard as PDF'}
          disabled={isExportingDashboard}
          loading={isExportingDashboard}
          onClick={exportDashboard}
          showToolbarOnDisabled
        />
      </Box>
      {activeWidget ? (
        <InteractiveView
          closeInteractive={() => setActiveWidget(null)}
          filterContext={filterContext}
          activeWidget={activeWidget}
          onSelect={selected => {
            if (activeWidget) {
              handleWidgetSelect(activeWidget.type, selected);
            }
          }}
        />
      ) : (
        <Box sx={{ ...flex.col, gap: 2, overflowY: 'auto' }}>
          <Box sx={{ ...flex.row, gap: 2, width: '100%', overflow: 'auto', minHeight: 137 }}>
            {Object.keys(statuses).map(s => (
              <Box key={s} sx={widgetBoxStyle}>
                {statuses[s]}
              </Box>
            ))}
          </Box>

          <Box sx={{ ...flex.row, gap: 2 }}>
            <Box
              sx={{
                ...widgetBoxStyle,
                flex: 0.9,
                backgroundColor: theme.palette.colors.neutrals[100],
                minHeight: 300,
              }}>
              <AlertToIncidents filterContext={filterContext} />
            </Box>

            <Box
              sx={{
                ...widgetBoxStyle,
                flex: 0.9,
                minHeight: 300,
              }}>
              <IncidentStatus
                filterContext={filterContext}
                onSelect={selected => {
                  handleWidgetSelect(InteractiveWidgetType.IncidentStatus, selected);
                }}
              />
            </Box>
            <Box
              sx={{
                ...widgetBoxStyle,
                flex: 1.2,
                minHeight: 300,
              }}>
              <MeanTimeToRespond
                filterContext={filterContext}
                onSelect={selected => {
                  handleWidgetSelect(InteractiveWidgetType.MeanTimeToRespond, selected);
                }}
              />
            </Box>
          </Box>

          <Box sx={{ ...flex.row, gap: 2 }}>
            <Box sx={{ ...widgetBoxStyle, minHeight: 400 }}>
              <Top5AttackedEntities
                filterContext={filterContext}
                onSelect={selected => {
                  handleWidgetSelect(InteractiveWidgetType.Top5AttackedEntities, selected);
                }}
              />
            </Box>
            <Box sx={{ ...widgetBoxStyle, backgroundColor: 'transparent' }}>
              <TopHighRiskIncidents filterContext={filterContext} />
            </Box>
          </Box>

          <Box sx={{ ...flex.row, gap: 2 }}>
            <Box sx={{ ...widgetBoxStyle, minHeight: 400 }}>
              <Top5VendorsByAlertsVsIncidents
                filterContext={filterContext}
                onSelect={selected => {
                  handleWidgetSelect(InteractiveWidgetType.Top5Vendors, selected);
                }}
              />
            </Box>
            <Box sx={{ ...widgetBoxStyle, minHeight: 400 }}>
              <AlertsMappedToTactic
                filterContext={filterContext}
                onSelect={selected => {
                  handleWidgetSelect(InteractiveWidgetType.AlertsMappedToTactic, selected);
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ThreatDashboard;
